import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatBytes, formatDateTime, formatNumber, formatTradingDay, importModeLabel } from './dailyDataUtils'

function buildPreviewQuery(opts, templateId) {
  const params = new URLSearchParams()
  if (opts.tradingDay === 'yesterday') params.set('trading_day', 'yesterday')
  else if (opts.tradingDay) params.set('trading_day', opts.tradingDay)
  if (opts.blobPath) params.set('blob_path', opts.blobPath)
  if (opts.importAllBatches) params.set('import_all_batches', 'true')
  if (templateId) params.set('template_id', templateId)
  return params.toString()
}

export default function ImportConfirmModal({ open, importOpts, templateId, onClose, onConfirm, confirming }) {
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !importOpts) {
      setPreview(null)
      setError(null)
      return undefined
    }

    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      setPreview(null)
      try {
        const qs = buildPreviewQuery(importOpts, templateId)
        const res = await authFetch(getLocalApiUrl(`/api/daily-data/patron/preview?${qs}`))
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to load preview')
        if (!cancelled) setPreview(json.data)
      } catch (e) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [open, importOpts, templateId])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={confirming ? undefined : onClose} aria-hidden />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Confirm DimPatron import</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
              <span className="ml-3 text-sm text-gray-600">Loading file summary…</span>
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">{error}</div>
          )}

          {preview && !loading && (
            <>
              {!preview.ready && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 space-y-2">
                  <p>{preview.error || 'No files found for this import.'}</p>
                  {preview.source_type === 'local' && (
                    <p>
                      DimPatron for psgdev should come from <strong>Azure SID blob</strong>, not a folder on this VM.
                      Open{' '}
                      <a href="/monitor-safe/daily-data/settings" className="underline font-medium">Daily Data settings</a>
                      {' '}→ set <strong>File source</strong> to <strong>Azure SID blob</strong>, save, then use the
                      Azure file list on this page (or pick a trading day that has blobs).
                    </p>
                  )}
                  {preview.source_type === 'azure_blob' && (
                    <div className="space-y-2">
                      <p>
                        The <strong>Recent trading days</strong> table is a calendar view — not every date has an Azure extract.
                        Use <strong>Ingest</strong> on a file in the Azure list, or pick a date below.
                      </p>
                      {(preview.available_trading_days || []).length > 0 && (
                        <div className="rounded border border-gray-200 bg-white p-3 text-xs">
                          <p className="font-medium text-gray-700 mb-1">Days with DimPatron in Azure:</p>
                          <ul className="space-y-1">
                            {preview.available_trading_days.map(d => (
                              <li key={d.trading_day} className="font-mono text-gray-600">
                                {d.trading_day} — {d.folder_name} ({d.file_count} file{d.file_count === 1 ? '' : 's'})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">Trading day</dt>
                  <dd className="font-medium text-gray-900">{formatTradingDay(preview.trading_day)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Import mode</dt>
                  <dd className="font-medium text-gray-900">{importModeLabel(preview.import_mode)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Source</dt>
                  <dd className="font-medium text-gray-900">
                    {preview.source_type === 'azure_blob' ? 'Azure SID blob' : 'Local directory'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Template</dt>
                  <dd className="font-medium text-gray-900">{preview.template_name || 'Platform default'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Venue code</dt>
                  <dd className="font-medium text-gray-900">{preview.venue_code || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Archived members</dt>
                  <dd className="font-medium text-gray-900">{preview.import_archived_members ? 'Included' : 'Skipped'}</dd>
                </div>
                {preview.import_strategy === 'insert_only' && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">Strategy</dt>
                    <dd className="font-medium text-gray-900">
                      New patrons only — existing member IDs are skipped (no sync from DimPatron)
                    </dd>
                  </div>
                )}
                {preview.import_strategy === 'sync_existing' && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">Strategy</dt>
                    <dd className="font-medium text-gray-900">
                      New patrons are added; existing patrons sync membership, expiry, tier, email, and venue phone from the file. Payout-captured mobile numbers are not changed.
                    </dd>
                  </div>
                )}
              </dl>

              {preview.source_type === 'azure_blob' && preview.azure && (
                <p className="text-xs text-gray-500 font-mono">
                  {preview.azure.container}/{preview.azure.prefix}
                </p>
              )}
              {preview.local_directory && (
                <p className="text-xs text-gray-500 font-mono">{preview.local_directory}</p>
              )}

              {preview.ready && (
                <>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-semibold text-gray-900">{preview.summary.file_count}</div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">File(s)</div>
                    </div>
                    <div>
                      <div className="text-2xl font-semibold text-gray-900">{formatBytes(preview.summary.total_bytes)}</div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Total size</div>
                    </div>
                    <div>
                      <div className="text-2xl font-semibold text-gray-900">
                        {preview.summary.total_estimated_rows != null
                          ? `~${formatNumber(preview.summary.total_estimated_rows)}`
                          : '—'}
                      </div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Est. rows</div>
                    </div>
                  </div>

                  {preview.import_strategy === 'insert_only' && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-2">
                      {importOpts?.importAllBatches
                        ? 'Files are imported one at a time with live progress. You can stop after the current file finishes. Only new member IDs are added; existing patrons are skipped.'
                        : 'DimPatron is not mirrored into PaySafeGO. Rows already in your patron list are skipped; only new member IDs are added.'}
                    </p>
                  )}
                  {preview.import_strategy === 'sync_existing' && (
                    <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded p-2">
                      {importOpts?.importAllBatches
                        ? 'Files import one at a time. New patrons are added; existing patrons get membership status, expiry, tier, email, and venue directory phone updated. Payout mobile capture stays on payout records only.'
                        : 'New patrons are added. Existing patrons sync membership, expiry, tier, email, and venue phone from DimPatron. Name, address, and compliance fields are preserved.'}
                    </p>
                  )}

                  {preview.summary.rows_estimated_from_size && (
                    <p className="text-xs text-gray-500">
                      Row counts are estimated from file size (~{Math.round(280)} bytes/row) — files are not loaded into memory for preview.
                    </p>
                  )}

                  {preview.summary.row_counts_partial && !preview.summary.rows_estimated_from_size && (
                    <p className="text-xs text-amber-700">
                      Row counts omitted for large files (&gt;20 MB). Size and file list are still shown.
                    </p>
                  )}

                  {preview.file_fallback && (
                    <p className="text-xs text-amber-700">
                      No exact file for this trading day — will use the latest file in the import directory.
                    </p>
                  )}

                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Size</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Est. rows</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Modified</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {preview.files.map(f => (
                          <tr key={f.path}>
                            <td className="px-3 py-2 font-mono text-xs text-gray-800 max-w-xs truncate" title={f.path}>
                              {f.name}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">{formatBytes(f.size)}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {f.estimated_rows != null ? formatNumber(f.estimated_rows) : '—'}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                              {formatDateTime(f.last_modified)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-xs text-gray-500">
                    Existing members are updated by member ID; new members are inserted. Compliance fields (risk, PEP) are not overwritten.
                  </p>
                </>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming || loading || !preview?.ready}
            className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {confirming ? 'Importing…' : importOpts?.importAllBatches ? 'Start batch ingest' : 'Start import'}
          </button>
        </div>
      </div>
    </div>
  )
}
